import React, { useState } from "react";
import "./InputTextArea.css";

export interface InputTextAreaProps {
  /**
   * Label text for the textarea
   */
  label?: string;

  /**
   * Placeholder text for the textarea
   */
  placeholder?: string;

  /**
   * Current value of the textarea
   */
  value?: string;

  /**
   * Callback function when the value changes
   */
  onChange?: (value: string) => void;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Whether there is an error
   */
  error?: boolean;

  /**
   * Error message to display
   */
  errorMessage?: string;

  /**
   * Size variant of the component
   */
  size?: "sm" | "md";

  /**
   * Additional CSS class
   */
  className?: string;
}

/**
 * Component for text area input with label and error states
 */
export const InputTextArea: React.FC<InputTextAreaProps> = ({
  label = "Label",
  placeholder = "Escriu aquí...",
  value = "",
  onChange,
  disabled = false,
  error = false,
  errorMessage,
  size = "md",
  className = "",
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(event.target.value);
  };

  const containerClasses = [
    "qida-input-textarea",
    `qida-input-textarea--${size}`,
    error ? "qida-input-textarea--error" : "",
    disabled ? "qida-input-textarea--disabled" : "",
    isFocused ? "qida-input-textarea--focused" : "",
    isHovered ? "qida-input-textarea--hovered" : "",
    value ? "qida-input-textarea--filled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses}>
      <div className="qida-input-textarea__input-container">
        <div className="qida-input-textarea__label">{label}</div>
        <textarea
          className="qida-input-textarea__input"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={disabled}
        />
      </div>

      {error && errorMessage && (
        <div className="qida-input-textarea__error-message">{errorMessage}</div>
      )}
    </div>
  );
};

export default InputTextArea;
